import dayjs from "dayjs";
import {
  Drawer,
  useTheme,
  useMediaQuery,
  Grid,
  Typography,
  Button,
  Autocomplete,
  TextField,
} from "@mui/material";
import { useUsers } from "@/hooks/useUsers";
import { DEFAULT_COLOR } from "@/constants/color";
import { Calendar, RefreshCw } from "lucide-react";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { useProjects } from "@/services/useProjects";
import { useTypes } from "@/services/useTypes";
import { useRegions } from "@/services/useRegions";
import {
  ASESOR_CIERRE_ROLE,
  ASESOR_ROLE,
  CORDINATOR_ROLE,
  GENERAL_PRINCIPAL_ROLE,
  MASTER_ROLE,
  REGIONAL_PRINCIPAL_ROLE,
  SUPERVISOR_ROLE,
} from "@/constants/roles";
import { useAuth } from "@/context/auth-context";
import { getRole } from "@/utils/func";

interface FilterDrawerProps {
  open: boolean;
  onClose: () => void;
  filterChange: (key: string, value: string | Date | null) => void;
  filters: Filter;
  handleResetFilters: () => void;
  showUsers?: boolean;
  showProject?: boolean;
}

export type Filter = {
  startDate?: Date;
  endDate?: Date;
  userSelected?: string;
  projectSelected?: string;
  type?: string;
  region?: string;
  cordination?: string;
  principal?: string;
  master?: string;
};

export const FilterDrawer: React.FC<FilterDrawerProps> = ({
  open,
  onClose,
  filterChange,
  filters,
  handleResetFilters,
  showUsers = false,
  showProject = false,
}) => {
  const theme = useTheme();
  const { user } = useAuth();
  const { data } = useUsers();
  const { data: campaigns } = useProjects();
  const { data: types } = useTypes();
  const { data: regions } = useRegions();
  const { isAdmin, isMaster, isCordinator, isRegionalPrincipal } = getRole(
    user?.role || ""
  );

  const coordinatorOptions =
    data
      .filter((u) => u.role._id === CORDINATOR_ROLE)
      .map((u) => ({
        name: `${u.name} ${u.lastName}`,
        _id: u._id,
      })) || [];

  const principalOptions =
    data
      .filter((u) =>
        [GENERAL_PRINCIPAL_ROLE, REGIONAL_PRINCIPAL_ROLE].includes(u.role._id)
      )
      .map((u) => ({
        name: `${u.name} ${u.lastName}`,
        _id: u._id,
      })) || [];
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const handleClose = () => {
    onClose();
  };
  return (
    <Drawer
      anchor="right"
      open={open}
      PaperProps={{
        sx: {
          width: isMobile ? "80vw" : "30vw",
          maxWidth: isMobile ? "80vw" : "30vw",
        },
      }}
      onClose={handleClose}
    >
      <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="es">
        <Grid container size={{ xs: 12 }} spacing={2} sx={{ p: 2 }}>
          <Typography
            variant="h4"
            fontWeight="bold"
            sx={{ color: DEFAULT_COLOR }}
          >
            Filtros
          </Typography>
          <Grid container spacing={2} alignItems="center">
            {(isMaster || isCordinator || isRegionalPrincipal || isAdmin) && (
              <Grid
                size={{ xs: 12 }}
                sx={{ display: showUsers ? "block" : "none" }}
              >
                <Autocomplete
                  options={
                    data
                      ?.filter(
                        (d) =>
                          d.role &&
                          [
                            ASESOR_CIERRE_ROLE,
                            ASESOR_ROLE,
                            MASTER_ROLE,
                            SUPERVISOR_ROLE,
                          ].includes(d.role._id)
                      )
                      .map((d) => `${d.name} ${d.lastName}`) || []
                  }
                  value={filters.userSelected || ""}
                  onChange={(_, value) =>
                    filterChange("userSelected", value || "")
                  }
                  fullWidth
                  renderInput={(params) => (
                    <TextField {...params} label="Asesor" />
                  )}
                />
              </Grid>
            )}
            {(isCordinator || isRegionalPrincipal || isAdmin) && (
              <Grid
                size={{ xs: 12 }}
                sx={{ display: showUsers ? "block" : "none" }}
              >
                <Autocomplete
                  options={
                    data
                      ?.filter(
                        (d) =>
                          d.role &&
                          [MASTER_ROLE, SUPERVISOR_ROLE].includes(d.role._id)
                      )
                      .map((d) => `${d.name} ${d.lastName}`) || []
                  }
                  value={filters.master || ""}
                  onChange={(_, value) => filterChange("master", value || "")}
                  fullWidth
                  renderInput={(params) => (
                    <TextField {...params} label="Master" />
                  )}
                />
              </Grid>
            )}
            {(isRegionalPrincipal || isAdmin) && (
              <Grid
                size={{ xs: 12 }}
                sx={{ display: showUsers ? "block" : "none" }}
              >
                <Autocomplete
                  options={coordinatorOptions?.map((d) => d.name) || []}
                  value={filters.cordination || ""}
                  onChange={(_, value) =>
                    filterChange("cordination", value || "")
                  }
                  fullWidth
                  renderInput={(params) => (
                    <TextField {...params} label="Coordinación" />
                  )}
                />
              </Grid>
            )}
            {isAdmin && (
              <Grid
                size={{ xs: 12 }}
                sx={{ display: showUsers ? "block" : "none" }}
              >
                <Autocomplete
                  options={principalOptions?.map((d) => d.name) || []}
                  value={filters.principal || ""}
                  onChange={(_, value) =>
                    filterChange("principal", value || "")
                  }
                  fullWidth
                  renderInput={(params) => (
                    <TextField {...params} label="Dirección" />
                  )}
                />
              </Grid>
            )}
            <Grid size={{ xs: 12 }}>
              <Autocomplete
                options={types.map((c) => c.name) || []}
                value={filters.type || ""}
                onChange={(_, value) => filterChange("type", value || "")}
                fullWidth
                renderInput={(params) => (
                  <TextField {...params} label="Origen de Contacto" />
                )}
              />
            </Grid>
            {isAdmin && (
              <Grid
                size={{ xs: 12 }}
                sx={{ display: showUsers ? "block" : "none" }}
              >
                <Autocomplete
                  options={regions.map((c) => c.name) || []}
                  value={filters.region || ""}
                  onChange={(_, value) => filterChange("region", value || "")}
                  fullWidth
                  renderInput={(params) => (
                    <TextField {...params} label="Región" />
                  )}
                />
              </Grid>
            )}
            <Grid
              size={{ xs: 12 }}
              sx={{ display: showProject ? "block" : "none" }}
            >
              <Autocomplete
                options={campaigns.map((c) => c.name) || []}
                value={filters.projectSelected || ""}
                onChange={(_, value) =>
                  filterChange("projectSelected", value || "")
                }
                fullWidth
                renderInput={(params) => (
                  <TextField {...params} label="Proyecto" />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <DatePicker
                label="Fecha de inicio (Desde)"
                value={filters?.startDate ? dayjs(filters.startDate) : null}
                onChange={(newValue) => {
                  const laDate = newValue
                    ? dayjs
                        .tz(
                          dayjs(newValue).format("YYYY-MM-DD"),
                          "America/Mexico_City"
                        )
                        .toDate()
                    : null;

                  filterChange("startDate", laDate);
                }}
                format="DD/MM/YYYY"
                slotProps={{
                  textField: {
                    fullWidth: true,
                    InputProps: {
                      startAdornment: (
                        <Calendar size={16} style={{ marginRight: 8 }} />
                      ),
                    },
                  },
                }}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <DatePicker
                label="Fecha de fin (Hasta)"
                value={filters?.endDate ? dayjs(filters.endDate) : null}
                onChange={(newValue) => {
                  const laDate = newValue
                    ? dayjs
                        .tz(
                          dayjs(newValue).format("YYYY-MM-DD"),
                          "America/Mexico_City"
                        )
                        .toDate()
                    : null;

                  filterChange("endDate", laDate);
                }}
                format="DD/MM/YYYY"
                slotProps={{
                  textField: {
                    fullWidth: true,
                    InputProps: {
                      startAdornment: (
                        <Calendar size={16} style={{ marginRight: 8 }} />
                      ),
                    },
                  },
                }}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Button
                variant="outlined"
                onClick={handleResetFilters}
                fullWidth
                startIcon={<RefreshCw size={16} />}
              >
                Limpiar filtros
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </LocalizationProvider>
    </Drawer>
  );
};
